import { ChangeEvent } from "react";
import { languages } from "src/constants/languages";
import { orderStatuses } from "src/constants/orderStatuses";
import { patientStatuses } from "src/constants/patientStatuses";
import { Database } from "src/supabase.types";

export type Pagination = {
  page: number;
  limit: number;
  handleChangePage: (_: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export type UserRole = Database["public"]["Tables"]["user_roles"]["Row"] & {
  role: Role;
};

export type TitrationSearchFilter = {
  product_id?: number;
  duration?: number;
  dosage?: number;
  order_by?: string;
  ascending?: boolean;
  price_id?: number;
};

export type FilterProps = {
  filter: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
};

export type FilterType =
  | {
      name: string;
      placeholder: string;
      type: "custom";
      component: React.ComponentType<FilterProps>;
    }
  | {
      name: string;
      placeholder: string;
      type: "text";
    }
  | {
      name: string;
      label: string;
      type: "checkbox";
    }
  | {
      name: string;
      placeholder: string;
      type: "select";
      values: string[];
    }
  | {
      name: string;
      placeholder: string;
      type: "select";
      objectValues: { name: string; value: string }[];
    }
  | {
      name: string;
      placeholder: string;
      type: "multiselect";
      values: string[];
    }
  | {
      name: string;
      placeholder: string;
      type: "autocomplete";
      values: string[];
    };

export type InputType<T extends object> =
  | {
      type: "text";
      multiline: true;
      minRows: number;
      name: string;
      label: string;
      placeholder?: string;
      required: boolean;
      disabled?: boolean;
      value: string;
      onChange: (v: string) => void;
    }
  | {
      type: "text";
      multiline?: false;
      name: string;
      label: string;
      placeholder?: string;
      required: boolean;
      disabled?: boolean;
      value: string;
      onChange: (v: string) => void;
    }
  | {
      type: "number";
      name: string;
      label: string;
      placeholder?: string;
      parse?: (raw: string) => number | null;
      required: boolean;
      value: number | null;
      onChange: (v: number | null) => void;
    }
  | {
      type: "checkbox";
      label: string;
      required: boolean;
      value: boolean;
      onChange: (v: boolean) => void;
    }
  | {
      type: "autocomplete";
      label: string;
      placeholder: string;
      options: T[];
      getOptionLabel: (option: T) => string;
      getOptionDescription?: (option: T) => string | undefined;
      required: boolean;
      value: T | null;
      onChange: (v: T | null) => void;
    }
  | {
      type: "select";
      label: string;
      options: { label: string; value: string }[];
      required: boolean;
      value: string;
      onChange: (v: string) => void;
    }
  | {
      label: string;
      type: "custom";
      component: () => React.ReactNode;
    };

export type UpdateBulkOrdersArg = {
  ids: number[];
  next_attempt_at?: string;
  status?: OrderStatus;
};

export type GetPatientProfilesArgs = {
  id?: string;
  order_by?: string;
  ascending?: boolean;
  region?: string;
  language?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  refill_month?: number;
  from: number;
  to: number;
};

export type UpdatePatientAddress =
  Database["public"]["Tables"]["shipping_address"]["Update"];

export type CreatePatientAddress =
  Database["public"]["Tables"]["shipping_address"]["Insert"];

export type USPSAddressCorrection = {
  code: string;
  text: string;
};

export type USPSAddress = {
  streetAddress: string;
  streetAddressAbbreviation: string;
  secondaryAddress: string;
  city: string;
  cityAbbreviation: string;
  state: string;
  postalCode: string;
  province: string;
  ZIPCode: string;
  ZIPPlus4: string;
  urbanization: string;
};

export type USPSAddressVerified = {
  firm: string | null;
  address: USPSAddress;
  corrections: USPSAddressCorrection[];
};

export type AutocompletePrediction = {
  description: string;
  place_id: string;
};

export type AddressDetails = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
};

export type Column<T> =
  | {
      id: string;
      label: string;
      minWidth?: number;
      orderable: true;
      orderBy: string; // required if orderable
      referencedTable?: string; //optional join table to order by
      align: "right" | "center" | "left";
      format: (value: T) => string;
      render?: (value: T) => React.ReactNode;
      default_sort?: string;
    }
  | {
      id: string;
      label: string;
      minWidth?: number;
      orderable: false;
      align: "right" | "center" | "left";
      format: (value: T) => string;
      default_sort?: string;
      render?: (value: T) => React.ReactNode;
    };

export function isOrderStatus(status: string): status is OrderStatus {
  return orderStatuses.includes(status as OrderStatus);
}

export function isPatientStatus(status: string): status is PatientStatus {
  return patientStatuses.includes(status as PatientStatus);
}

export function isLanguage(language: string): language is Languages {
  return languages.includes(language.toLowerCase() as Languages);
}

// type RequiredNonNullable<T> = {
//   [K in keyof T]-?: NonNullable<T[K]>;
// };

export type CustomError = {
  data: {
    message: string;
  };
  status: number;
};

export function isCustomError(err: unknown): err is CustomError {
  return typeof err === "object" && err != null && "data" in err;
}

export type PrescriptionInstruction =
  Database["public"]["Tables"]["prescription_instructions"]["Row"];

export type PrescriptionMedicationInstruction = Pick<
  Database["public"]["Tables"]["prescriptions"]["Row"],
  "id"
> & {
  medications: Medication[];
  instructions: PrescriptionInstruction[];
  pharmacy: Pharmacy;
};

export type CouponDetails = {
  id: number;
  amount: number;
  created_at: string;
  redeemed: number;
  is_active: boolean;
  name: string;
  product_name: string;
};

export type Intake = Omit<
  Database["public"]["Tables"]["intakes"]["Row"],
  "first_question"
> & {
  product: {
    slug: string;
    name: string;
  };
  first_question: {
    title: string;
    slug: string;
  };
};

export type IntakeQuestion = Question & {
  question_id: number;
  question_position: number | null;
  next_question: {
    title: string;
    slug: string;
  } | null;
};

export type SmsMedia = Pick<
  Database["public"]["Tables"]["sms_messages_media"]["Row"],
  "storage_path" | "external_id"
>;
export type SmsMessage = Database["public"]["Tables"]["sms_messages"]["Row"];
export type SmsMessageWithSender = SmsMessage & {
  sender: Profile;
  media?: SmsMedia[];
};

export type UpdateIntakeQuestion =
  Database["public"]["Tables"]["intake_questions"]["Update"];

export type AbTest = Database["public"]["Tables"]["ab_tests"]["Row"] & {
  products: { id: number; name: string }[];
};

export type AbTestUpdate =
  Database["public"]["Tables"]["ab_tests"]["Update"] & {
    products: { id: number; name: string }[];
  };

export type AbTestVariant =
  Database["public"]["Tables"]["ab_test_variants"]["Row"];

export type AbTestVariantName =
  Database["public"]["Enums"]["ab_test_variant_name"];

export type Coupon = Database["public"]["Tables"]["coupons"]["Row"] & {
  product: Product;
};

export type PatientCoupon =
  Database["public"]["Tables"]["patient_coupons"]["Row"] & {
    coupon: Coupon;
    product: Product;
  };
export type PatientCouponInsert =
  Database["public"]["Tables"]["patient_coupons"]["Insert"];

export type PatientCouponUpdate =
  Database["public"]["Tables"]["patient_coupons"]["Update"];

type WithRequired<T, K extends keyof T> = Omit<T, K> & {
  [P in K]-?: NonNullable<T[P]>;
};

export type Profile = Database["public"]["Tables"]["profiles"]["Row"];

export type ProviderProfile =
  Database["public"]["Tables"]["providers"]["Row"] & {
    profile: Profile;
  };

export type ProvidersWithRoles =
  Database["public"]["Tables"]["providers"]["Row"] & {
    profile: Profile & {
      roles: string[];
    };
  };

export type States = Database["public"]["Tables"]["states"]["Row"];
export type ShippingAddress =
  Database["public"]["Tables"]["shipping_address"]["Row"];
export type MessageThreads =
  Database["public"]["Tables"]["message_threads"]["Row"];

export type MessageType = Pick<
  Database["public"]["Tables"]["messages"]["Row"],
  "created_at" | "message" | "public_id"
> & {
  sender: Pick<
    Database["public"]["Tables"]["profiles"]["Row"],
    "first_name" | "last_name" | "id" | "preferred_name"
  >;
};

export type PatientProfile = Database["public"]["Tables"]["patients"]["Row"] & {
  profile: Profile;
  subscriptions: string[];
  products: string[];
};

export type GetMessageThreads = {
  id: number;
  is_active: boolean;
  slug: string;
  last_message: string | null;
  product_category: ProductCategory;
  product: {
    name: string;
  };
};

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type CreateMassageArgs = Pick<
  Database["public"]["Tables"]["messages"]["Insert"],
  "message" | "require_response" | "language" | "is_phi"
> & {
  thread_slug: string;
  current_queue?: string;
};

export type ProductVariant =
  Database["public"]["Tables"]["product_variants"]["Row"];

export type ProductPrice = Database["public"]["Tables"]["prices"]["Row"] & {
  coupon: {
    amount: number;
    stripe_id: string;
    name: string;
    id: number;
  } | null;
  product: {
    slug: string;
  };
  titration?: {
    min_dose: number;
    max_dose: number;
    dose_unit: string;
  }[];
};

export type CreatePatientSubscriptionArgs = {
  price: string;
  status: string;
  coupon_id?: number;
  initial?: boolean;
  patient_id: number;
};

export type CreatePrescriptionArgs = Omit<
  Database["public"]["Tables"]["prescriptions"]["Insert"],
  "dispense_quantity"
> & {
  dispense_quantity?: string;
};

export type PatientSubscriptions =
  Database["public"]["Tables"]["patient_subscriptions"]["Row"] & {
    product: { name: string; slug: string };
    price: Omit<ProductPrice, "product" | "product_category_id">;
    product_category: { name: string };
  };

export type UpdatePatientSubscription =
  Database["public"]["Tables"]["patient_subscriptions"]["Update"];

export type PatientSubscriptionsWithPatient =
  Database["public"]["Tables"]["patient_subscriptions"]["Row"] & {
    patient: PatientProfile;
    product: Pick<Product, "slug">;
  };

export type Titration =
  Database["public"]["Tables"]["titration_schedule"]["Row"] & {
    product: { slug: string; name: string };
    price?: { amount: number };
  };

export type Task = Omit<
  Database["public"]["Tables"]["tasks"]["Row"],
  "metadata"
> & {
  metadata?: Record<string, string>;
};

export type ClinicianTasksView =
  Database["public"]["Views"]["tasks_view"]["Row"];

export type TaskView = WithRequired<ClinicianTasksView, "id">;

export type TaskWithPatient = Task & {
  patient: PatientProfile;
};

export type WaitingRoomPatient =
  Database["public"]["Tables"]["waiting_room"]["Row"] & {
    patient: PatientProfile;
  };

export type WaitingRoomView = WithRequired<
  Database["public"]["Views"]["waiting_room_view"]["Row"],
  "id" | "status"
>;

export type UpdateWaitingRoom =
  Database["public"]["Tables"]["waiting_room"]["Update"] & {
    ids: number[];
    isForwarded?: boolean;
  };

export type WaitingRoomStatus =
  Database["public"]["Enums"]["waiting_room_status"];

export type UpdateIdVerification =
  Database["public"]["Tables"]["id_verification"]["Update"];

export type IdVerification =
  Database["public"]["Tables"]["id_verification"]["Row"];

export type UpdateWaitingRoomPatient =
  Database["public"]["Tables"]["waiting_room"]["Update"];
export type Medication = Database["public"]["Tables"]["medications"]["Row"] & {
  pharmacy: { name: string; active: boolean };
  medication_strength: MedicationStrength[];
  vial_quantity: number | undefined;
  treatment: Pick<Treatment, "name">;
};

export type MedicationWithProduct = Medication & { product: Product[] };

export type DaysSupply = Database["public"]["Tables"]["days_supply"]["Row"];

export type CreateDaysSupply =
  Database["public"]["Tables"]["days_supply"]["Insert"];

export type UpdateMedication = Omit<
  Database["public"]["Tables"]["medications"]["Update"],
  "id"
>;

export type CreateMedication = Omit<
  Database["public"]["Tables"]["medications"]["Insert"],
  "id"
>;

export type GetMedicationsArgs = {
  order_by?: string;
  ascending?: boolean;
  pharmacy?: string;
  dispense_unit?: string;
  name?: string;
  form?: string;
  treatment?: string;
  dosage?: string;
  from?: number;
  to?: number;
  gender?: string | null;
};

export type MedicalSupply = Database["public"]["Tables"]["supplies"]["Row"];
export type MedicationStrength =
  Database["public"]["Tables"]["medication_strengths"]["Row"];
export type CreateMedicationStrength =
  Database["public"]["Tables"]["medication_strengths"]["Insert"];

export type PharmacyWithMedicationsAndSupplies =
  Database["public"]["Tables"]["pharmacies"]["Row"] & {
    medications?: Medication[];
    supplies?: MedicalSupply[];
  };

export type PharmacyState =
  Database["public"]["Tables"]["pharmacy_states"]["Row"];
export type Pharmacy = Database["public"]["Tables"]["pharmacies"]["Row"] & {
  pharmacy_states: PharmacyState[];
};

export type UpdatePharmacy = Omit<
  Database["public"]["Tables"]["pharmacies"]["Update"],
  "id"
>;

export type GetPharmaciesArgs =
  | {
      product_id: number;
      states?: string[];
    }
  | undefined;

export type BillingModelEnums =
  Database["public"]["Enums"]["billing_model_type"];

export const BillingModel: BillingModelEnums[] = ["one_time", "subscription"];

export type Prescription = Omit<
  Database["public"]["Tables"]["prescriptions"]["Row"],
  "metadata"
> & {
  metadata?: Record<string, string>;
  provider?: {
    profile: {
      first_name: string;
      last_name: string;
    };
  };
} & {
  product: {
    name: string;
    billing_model: BillingModelEnums;
    slug: string;
    is_glp1: boolean;
  };
  product_category: {
    id: number;
    name: string;
  };
};

export type PrescriptionWithMedication = Prescription & {
  medications?: {
    medication: {
      id: number;
      name: string;
      dosage: string;
      dispense_unit: string;
      form: MedicationForm;
    };
  }[];
  pharmacy?: Pharmacy;
};

export type UpdatePrescriptionInstructions = Pick<
  PrescriptionInstruction,
  "amount" | "dosage" | "duration" | "value"
>[];

export type UpdatePrescriptionArgs =
  Database["public"]["Tables"]["prescriptions"]["Update"] & {
    supply_id?: number | null;
    instructions?: Pick<
      PrescriptionInstruction,
      "amount" | "dosage" | "duration" | "value" | "interval" | "interval_count"
    >[];
    medication_ids?: number[];
  };

export type PrescriptionMedication =
  Database["public"]["Tables"]["prescription_medications"]["Row"];

export type PrescriptionMedicationCreate =
  Database["public"]["Tables"]["prescription_medications"]["Insert"] & {
    vial_quantity?: number;
    pharmacy_id?: number;
  };

export type Order = Database["public"]["Tables"]["orders"]["Row"];
export type OrderStatus = Database["public"]["Enums"]["order_status"];

export enum OrderTypeEnum {
  Initial = "initial",
  Refill = "refill",
  Upsell = "upsell",
}

export type OrderWithPrescription = Order & {
  prescription: Prescription & {
    medications: { instruction: Instructions; medication: Medication }[];
  } & {
    provider: Database["public"]["Tables"]["providers"]["Row"] & {
      profile: Database["public"]["Tables"]["profiles"]["Row"];
    };
  };
} & {
  pharmacy: { name: string };
  product: {
    display_name: string;
    billing_model: BillingModelEnums;
    slug: string;
  };
  external_pharmacy?: { name: string };
};

export type FilesFromStorage = {
  name: string;
  url: string;
};

export type Encounter = Database["public"]["Tables"]["encounters"]["Row"];
export type EncounterStatus = Database["public"]["Enums"]["encounter_status"];

export type CreateOrder = Database["public"]["Tables"]["orders"]["Insert"];

export type OnlineVisit = Omit<
  Database["public"]["Tables"]["online_visits"]["Row"],
  "metadata"
> & {
  product: {
    name: string;
    slug: string;
    billing_model: BillingModelEnums;
  };
  product_category: ProductCategory;
  profile: Profile;
  metadata?: Record<string, string>;
};

export type OnlineVisitCreate = {
  product: string;
  patient_id: number;
  visit_type?: VisitType;
  metadata?: Record<string, unknown>;
};

export type SubscriptionReactivation =
  Database["public"]["Tables"]["scheduled_actions"]["Row"];

export type Question = Database["public"]["Tables"]["questions"]["Row"];
export type QuestionType = Database["public"]["Enums"]["question_type"];
export type QuestionInsert =
  Database["public"]["Tables"]["questions"]["Insert"];

export type QuestionOption =
  Database["public"]["Tables"]["question_options"]["Row"];

export type IntakeQuestionOption =
  Database["public"]["Tables"]["question_options"]["Row"] & {
    position: number;
    next_question: {
      title: string;
      slug: string;
    } | null;
  };

export type QuestionOptionInsert =
  Database["public"]["Tables"]["question_options"]["Insert"];

export type IntakeQuestionOptionUpdate =
  Database["public"]["Tables"]["intake_question_options"]["Update"];

export type Answer = Pick<
  Database["public"]["Tables"]["answers"]["Row"],
  "question_id" | "question_type" | "visit_id"
> & {
  response: {
    answer: string[];
    details?: string;
  };
  question: Pick<
    Database["public"]["Tables"]["questions"]["Row"],
    "title" | "tag"
  > & {
    options: Pick<QuestionOption, "label" | "value">[] | null;
  };
};

export type GetAnswersArgs =
  | {
      patient_id: number | null;
    }
  | {
      visit_id: string;
    };

export type PatientWeight =
  Database["public"]["Tables"]["weight_tracker"]["Row"];

export type UpdatePatientWeight = Omit<
  Database["public"]["Tables"]["weight_tracker"]["Update"],
  "id" | "patient_id"
> & {
  id: number;
  patient_id: number;
};

export type createPatientWeight = Omit<
  Database["public"]["Tables"]["weight_tracker"]["Insert"],
  "id" | "patient_id"
> & {
  patient_id: number;
};

export type GetPatientWeightArgs = {
  patient_id: number;
};

export type ClinicalNotes =
  Database["public"]["Tables"]["clinical_notes"]["Row"] & {
    profile: Profile;
  };

export type CreateClinicalNotes =
  Database["public"]["Tables"]["clinical_notes"]["Insert"];
export type UpdateClinicalNotes =
  Database["public"]["Tables"]["clinical_notes"]["Update"];

export type UploadFile = {
  bucket: string;
  path: string;
  file: File;
  uploadedBy?: string;
  title?: string;
};

export type FileStorage = Database["public"]["Tables"]["file_storage"]["Row"];

export type FileType = {
  fileToUpload: string | File | Blob;
  type: string;
  name: string;
};

export type BucketProps = {
  bucket: string;
  path: string;
};

export type BucketFile = {
  url: string;
  type: string;
  name: string;
};

export type UpdateFile = Database["public"]["Tables"]["file_storage"]["Update"];

export type PatientStatus = Database["public"]["Enums"]["patient_status"];
export type ProductCategory = Omit<
  Database["public"]["Tables"]["product_categories"]["Row"],
  "created_at"
>;

export type Product = Pick<
  Database["public"]["Tables"]["products"]["Row"],
  | "name"
  | "id"
  | "slug"
  | "stripe_product_id"
  | "billing_model"
  | "display_name"
  | "gender"
  | "status"
  | "launched_at"
  | "is_glp1"
  | "insurance_based"
  | "is_e_prescribe"
> & {
  category: Omit<ProductCategory, "created_at">;
};

export type Languages = Database["public"]["Enums"]["languages"];

export type PromoBanner = Database["public"]["Tables"]["banners"]["Row"] & {
  product: Product;
};

export type CreatePromoBanner =
  Database["public"]["Tables"]["banners"]["Insert"];

export type UpdatePromoBanner =
  Database["public"]["Tables"]["banners"]["Update"];

export type BannerStatus = Database["public"]["Enums"]["banner_status"];

export type Instructions = Database["public"]["Tables"]["instructions"]["Row"];

export type DosageInstructions = Pick<
  Database["public"]["Tables"]["instruction_medications"]["Row"],
  "dispense_quantity" | "instruction_id"
> & {
  instruction: Instructions;
};

export type ProviderSpecialty =
  Database["public"]["Tables"]["provider_specialties"]["Row"];

export type Specialties = Database["public"]["Enums"]["specialties"];

export type ProviderSpecialtyUpdate =
  Database["public"]["Tables"]["provider_specialties"]["Update"];

export type ManualChargeInvoiceArgs = {
  patient_id: number;
  amount: number;
  description: string;
  payment_method_id: string;
  product_id: number;
  product_category_id: number;
};

export type StripeInvoice = {
  id: string;
  status: string;
  created: number;
  amount_paid: number;
  amount_due: number;
  invoice_pdf: string;
  payment_intent: {
    created: number;
    payment_method: {
      card: {
        brand: string;
        last4: string;
      };
    };
    latest_charge: {
      created: number;
    };
  };
  charge: {
    amount_refunded: number;
    refunded: boolean;
    payment_method: string;
    payment_method_details: {
      card: {
        brand: string;
        last4: string;
      };
    };
  };
  description: string;
};

export type CreateCreditNoteArgs = {
  invoice: string;
  amount: number;
  memo: string;
};

export type ProfileWithRoles = Profile & {
  roles: { name: string; display_name: string | null }[];
} & {
  provider?: Omit<
    Database["public"]["Tables"]["providers"]["Row"],
    "profile_id"
  >;
};

export type Employee = Database["public"]["Tables"]["profiles"]["Row"] & {
  roles: { name: string; display_name: string | null }[];
};

export type ProviderAvailability =
  Database["public"]["Tables"]["provider_availability"]["Row"];

type AvailabilityWindow = {
  start_time: string;
  end_time: string;
};

export type ProviderAvailabilityInsert = {
  provider_id: number;
  date: string;
  windows: AvailabilityWindow[];
};

export type ProviderAvailabilityUpdate =
  Database["public"]["Tables"]["provider_availability"]["Update"];

export type TimeSlot = Database["public"]["Tables"]["time_slots"]["Row"];

export type ProviderLicense =
  Database["public"]["Tables"]["provider_licenses"]["Row"];

export type ProviderLicenseUpdate =
  Database["public"]["Tables"]["provider_licenses"]["Update"];

export type Provider = Database["public"]["Tables"]["providers"]["Row"];

export type DetailedEmployee = Employee & {
  provider?: Provider;
};

export type EmployeeUpdate =
  Database["public"]["Tables"]["profiles"]["Update"] & {
    roles: number[];
    npi_key?: string;
  };

export type Role = Database["public"]["Tables"]["roles"]["Row"];

export type EmployeesView = WithRequired<
  Omit<Database["public"]["Views"]["employees"]["Row"], "roles">,
  "id"
> & { roles: { name: string; display_name: string | null }[] };

export type MedicationForm = Database["public"]["Enums"]["medication_form"];

export const MedicationFormEnums: MedicationForm[] = [
  "oral",
  "topical",
  "dropper",
  "injectable",
];

export type PaymentMethodType = {
  id: string;
  brand: string;
  isDefault: boolean;
  last4: string;
  expiration: string;
};

export type PaymentIntent = {
  client_secret: string;
};

export type PayStripeInvoice = {
  id: string;
  status: string;
  created: number;
  amount_paid: number;
  amount_due: number;
  invoice_pdf: string;
  payment_intent: {
    payment_method: {
      card: {
        brand: string;
        last4: string;
      };
    };
  };
  charge: {
    payment_method_details: {
      card: {
        brand: string;
        last4: string;
      };
    };
  };
  description: string;
};

export type Dispute = {
  id: string;
  created: number;
  status: string;
  amount: number;
  reason: string;
  charge: {
    created: number;
    invoice: {
      description: string;
    };
  };
};

export type StripeInvoiceError = {
  type: string;
  raw: { message: string };
  code?: string;
  decline_code?: string;
  doc_url?: string;
  statusCode?: number;
  requestId?: string;
};

export type OneTimePaymentSuccessArgs = {
  patient: Pick<PatientProfile, "id" | "profile">;
  body: {
    product_id: number;
    product_category_id: number;
    billing_model: BillingModelEnums;
    dispense_quantity?: string;
    days_supply?: number;
    metadata?: {
      dosage?: string;
      invoice_id: string;
      form?: string;
      prescription_type?: OrderTypeEnum;
    };
    approval_required: boolean;
  };
};

export type TaskTypeEnum = Database["public"]["Enums"]["task_types"];

export enum TaskType {
  UnreadMessage = "UNREAD_MESSAGE",
  PrescriptionRequest = "PRESCRIPTION_REQUEST",
  PrescriptionReview = "PRESCRIPTION_REVIEW",
  SendDeniedPr = "SEND_DENIED_PR",
  IdVerification = "ID_VERIFICATION",
  SubmitOrder = "SUBMIT_ORDER",
  AddTracking = "ADD_TRACKING",
  UpdatePayment = "UPDATE_PAYMENT",
  CheckIn = "CHECK-IN",
  Refill = "REFILL",
  UnpaidOrder = "UNPAID_ORDER",
  ReactivateMembership = "REACTIVATE_MEMBERSHIP",
  SendMessage = "SEND_MESSAGE",
  PriorAuthRequest = "PRIOR_AUTH_REQUEST",
  PriorAuthApproved = "PRIOR_AUTH_APPROVED",
  PriorAuthDenied = "PRIOR_AUTH_DENIED",
  PriorAuthUpdate = "PRIOR_AUTH_UPDATE",
  PriorAuthClosed = "PRIOR_AUTH_CLOSED",
  PriorAuthCanceled = "PRIOR_AUTH_CANCELED",
  SyncVisitRequest = "SYNC_VISIT_REQUEST",
  EPrescriptionRequest = "E_PRESCRIPTION_REQUEST",
  UnreadTextMessage = "UNREAD_TEXT_MESSAGE",
}

export type NoteTypeEnums = Database["public"]["Enums"]["note_types"];

export type TreatmentTypes = Database["public"]["Enums"]["treatment_types"];

export const ProductCategories = {
  Nausea: "Nausea",
  WeightLoss: "Weight Loss",
  HairLoss: "Hair Loss",
  BirthControl: "Birth Control",
  EmergencyContraception: "Emergency Contraception",
  ErectileDysfunction: "Erectile Dysfunction",
  MentalHealth: "Mental Health",
  SkinCare: "Skin Care",
  Supplements: "Supplements",
  Sleep: "Sleep",
};

export type CreateProduct = Omit<
  Database["public"]["Tables"]["products"]["Insert"],
  "stripe_product_id"
>;

export type UpdateProduct = Omit<
  Database["public"]["Tables"]["products"]["Update"],
  "stripe_product_id"
>;

export type CreatePrice = Omit<
  Database["public"]["Tables"]["prices"]["Insert"],
  "stripe_id" | "product_id" | "recurring" | "product_category_id"
> & { product_slug: string };

export type UpdatePrice = Database["public"]["Tables"]["prices"]["Update"];

export type ProductPriceParams = {
  slug: string;
  sort_order?: string;
  active?: boolean;
};

export type SubscriptionStatus =
  Database["public"]["Enums"]["subscription_status"];

export type CreateTitration =
  Database["public"]["Tables"]["titration_schedule"]["Insert"];

export type VisitType = Database["public"]["Enums"]["visit_type"];

export type CreateProductMedication = Omit<
  Database["public"]["Tables"]["product_medications"]["Insert"],
  "product_id"
> & { product: string };

export type MedicationTitration =
  Database["public"]["Tables"]["medication_titrations"]["Row"] & {
    titration: Titration;
    pharmacy: { name: string; active: boolean };
    medication: Medication;
  };

export type ProductStatus = Database["public"]["Enums"]["product_status"];

export type CreateDraftPrescription = Omit<CreatePrescriptionArgs, "status"> & {
  due_at: string;
  price_id: number;
};

export type UpdateIntake = Database["public"]["Tables"]["intakes"]["Update"];

export type ProductCategoryCreate =
  Database["public"]["Tables"]["product_categories"]["Insert"];

export type Treatment = Database["public"]["Tables"]["treatments"]["Row"];

export type TreatmentCreate =
  Database["public"]["Tables"]["treatments"]["Insert"];

export const Treatments = {
  BirthControl: "Birth control",
  WeightLoss: "Weight loss",
  FemaleHairJoss: "Female hair loss",
  Nausea: "Nausea",
  MaleHairLoss: "Male hair loss",
  ErectileDysfunction: "Erectile dysfunction",
  MentalHealth: "Mental health",
  SkinCare: "Skin care",
  Supplements: "Supplements",
  Sleep: "Sleep",
};

export type OptionState = {
  position: number;
  questionOption: QuestionOption | null;
  nextQuestion: Question | null;
};

export type PharmacyProvider =
  Database["public"]["Tables"]["pharmacy_providers"]["Row"] & {
    pharmacy: { name: string };
  };

export type PharmacyPatient = Pick<
  Database["public"]["Tables"]["pharmacy_patients"]["Row"],
  "external_id" | "patient_id"
>;

export type AddEPrescribeClinician = {
  FirstName: string;
  LastName: string;
  DateOfBirth: string;
  NPINumber: string | null;
  ClinicianRoleType: string[];
};

export type AddDosespotPatient = {
  FirstName: string;
  LastName: string;
  DateOfBirth: Date;
  Address1: string;
  Address2: string | null;
  City: string;
  State: string;
  ZipCode: string;
  PrimaryPhone: string;
  NonDoseSpotMedicalRecordNumber: string;
  Active: boolean;
  Gender: string;
  PrimaryPhoneType: string;
};

export type PatientPreferredPharmacy =
  Database["public"]["Tables"]["patient_preferred_pharmacies"]["Row"];

export type PatientPreferredUpdate =
  Database["public"]["Tables"]["patient_preferred_pharmacies"]["Update"];

export type PatientPreferredCreate =
  Database["public"]["Tables"]["patient_preferred_pharmacies"]["Insert"];

export type EPrescribePharmacy = {
  PharmacyId: number;
  StoreName: string;
  Address1: string;
  Address2: string;
  City: string;
  State: string;
  ZipCode: string;
  PharmacySpecialties: string[];
};

export type EPrescribePriorAuth = {
  Status: string;
  AdditionalInfo: string;
  CreatedDateStamp: string;
  Attachment: {
    AttachmentId: number;
    CreatedDateStamp: string;
    FilePath: string;
    IsOutbound: boolean;
  };
};

export type PriorAuthorization =
  Database["public"]["Tables"]["prior_authorizations"]["Row"];
export type Invoice = Database["public"]["Tables"]["invoices"]["Row"];

export type PaymentInformation =
  Database["public"]["Tables"]["payment_information"]["Row"];

export type PatientRating =
  Database["public"]["Tables"]["patient_ratings"]["Row"];

export type RatingStatus = Database["public"]["Enums"]["rating_status"];

export type ClientSecret = {
  clientSecret: string | null;
};

export type SetDefaultPaymentMethodReturn = {
  id: string;
  isDefault: boolean;
};

export type SetDefaultPaymentMethodBody = {
  paymentMethodId: string;
  customerId: string;
};

export type ConversationTurn = {
  id: string;
  question: string;
  answer: string;
  createdAt: string;
  files?: AiInteractionFile[];
};
export type ResumeResult = {
  conversation_id: string | null;
  messages: ConversationTurn[];
};

export type ConversationThread =
  Database["public"]["Tables"]["conversation_threads"]["Row"];

export type ConversationThreadCreate =
  Database["public"]["Tables"]["conversation_threads"]["Insert"];

export type AlertProps = {
  display: boolean;
  message: string;
  severity: "success" | "error";
  onSuccess?: () => Promise<void>;
};

export type AiInteractionFile =
  Database["public"]["Tables"]["ai_interaction_files"]["Row"] & {
    signedUrl: string;
  };
